import { formatDateShortDD } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

type RecordLike = Record<string, unknown>;

export type ChecklistStatus = 'approved' | 'needsApproval' | 'blocked';
export type MarginBand = 'red' | 'yellow' | 'green';

export interface EgsMarginChecklistRow {
  collectedBy?: string;
  id: string;
  costPrice?: number;
  currencyCode: string;
  field: 'Extra Guest Surcharges margin';
  margin?: number;
  name: string;
  opportunityName?: string;
  sellPrice?: number;
  status: ChecklistStatus;
  statusLabel: string;
  marginBand?: MarginBand;
}

export interface EgsCapacityChecklistRow {
  egsCapacity?: number;
  field: 'EGS capacity';
  id: string;
  includedCapacity?: number;
  maxRoomCapacity?: number;
  name: string;
  roomRateId?: string;
  roomName: string;
  roomTypeId?: string;
  status: ChecklistStatus;
  statusLabel: string;
}

export const EGS_MARGIN_FIELD = 'Extra Guest Surcharges margin';
export const EGS_CAPACITY_FIELD = 'EGS capacity';

const EXTRA_GUEST_CAPACITY_FIELDS = [
  'extra_guest_capacity',
  'extra_guest_cap',
  'extra_guest_count',
  'extra_guests',
  'max_extra_guests',
  'max_extra_guest_capacity',
  'egs_capacity',
];

const INCLUDED_CAPACITY_FIELDS = [
  'included_capacity',
  'included_guests',
  'max_included_guests',
  'max_guests_included',
  'number_of_guests',
];

const MAX_ROOM_CAPACITY_FIELDS = ['max_capacity', 'max_occupancy', 'capacity', 'max_guests'];

export function calculateMarginPercentage(costPrice?: number, sellPrice?: number) {
  if (!isPositiveNumber(sellPrice) || !isFiniteNumber(costPrice)) {
    return undefined;
  }

  return ((sellPrice - costPrice) / sellPrice) * 100;
}

export function getMarginBand(margin?: number): MarginBand | undefined {
  if (!isFiniteNumber(margin)) {
    return undefined;
  }

  if (margin <= 10) {
    return 'red';
  }

  if (margin >= 19) {
    return 'green';
  }

  return 'yellow';
}

export function getMarginStatus(marginBand?: MarginBand): ChecklistStatus {
  if (!marginBand) {
    return 'blocked';
  }

  return marginBand === 'green' ? 'approved' : 'needsApproval';
}

export function buildEgsMarginRows(surcharges: Array<SalesforceSurchargePeriod>): Array<EgsMarginChecklistRow> {
  return surcharges.map((surcharge) => {
    const costPrice = isFiniteNumber(surcharge.converted_cost) ? surcharge.converted_cost : surcharge.cost_price;
    const sellPrice = surcharge.sell_price;
    const margin = calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      collectedBy: surcharge.collected_by,
      id: String(surcharge.id),
      costPrice,
      currencyCode: surcharge.currency_code || 'AUD',
      field: EGS_MARGIN_FIELD,
      margin,
      marginBand,
      name: surcharge.name || surcharge.opportunity_name || String(surcharge.id),
      opportunityName: surcharge.opportunity_name,
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

export function buildEgsCapacityRows({
  packages,
  propertyRoomTypes,
  surcharges,
}: {
  packages?: Array<App.Package>;
  propertyRoomTypes?: Array<App.RoomType>;
  surcharges: Array<SalesforceSurchargePeriod>;
}): Array<EgsCapacityChecklistRow> {
  const surchargesEgsCapacity = getMaxDefinedNumber(
    surcharges.map((surcharge) => readNumber(surcharge as unknown as RecordLike, EXTRA_GUEST_CAPACITY_FIELDS)),
  );

  return (packages ?? []).map((offerPackage, index) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const roomTypeId = readString(packageRecord, ['fk_room_type_id']);
    const roomRateId = readString(packageRecord, ['fk_room_rate_id']);
    const roomType = propertyRoomTypes?.find((room) => String(room.id) === roomTypeId);
    const roomRate = findRoomRate(roomType, roomRateId);
    const includedCapacity =
      readNumber(packageRecord, INCLUDED_CAPACITY_FIELDS) ??
      readIncludedGuestsCapacity(roomRate) ??
      readNumber((roomType ?? {}) as RecordLike, ['max_included_guests']);
    const egsCapacity = readNumber(packageRecord, EXTRA_GUEST_CAPACITY_FIELDS) ?? surchargesEgsCapacity;
    const maxRoomCapacity = getMaxRoomCapacity(roomType);
    const status =
      isFiniteNumber(includedCapacity) && isFiniteNumber(egsCapacity) && isFiniteNumber(maxRoomCapacity)
        ? maxRoomCapacity === includedCapacity + egsCapacity
          ? 'approved'
          : 'needsApproval'
        : 'blocked';

    return {
      egsCapacity,
      field: EGS_CAPACITY_FIELD,
      id: `${String(offerPackage.id_salesforce_external ?? offerPackage.le_package_id ?? index)}:${roomTypeId ?? ''}:${
        roomRateId ?? ''
      }`,
      includedCapacity,
      maxRoomCapacity,
      name: offerPackage.deal_option_name || offerPackage.name || `Package ${index + 1}`,
      roomRateId,
      roomName: roomType?.name || 'Room not mapped',
      roomTypeId,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

export function getStatusLabel(status: ChecklistStatus) {
  switch (status) {
    case 'approved':
      return 'Auto-approved';
    case 'needsApproval':
      return 'CM approval required';
    case 'blocked':
      return 'Missing data';
    default:
      return 'Unknown';
  }
}

export function formatOptionalNumber(value?: number) {
  return isFiniteNumber(value) ? String(value) : 'N/A';
}

export function formatMoney(value?: number, currencyCode = 'AUD') {
  return isFiniteNumber(value) ? `${currencyCode} ${value}` : 'N/A';
}

export function formatContextDate(value?: string) {
  return value ? formatDateShortDD(value) : 'Not available';
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function getMaxRoomCapacity(roomType?: App.RoomType) {
  if (!roomType) {
    return undefined;
  }

  const roomTypeRecord = roomType as unknown as RecordLike;
  const capacityFromField = readNumber(roomTypeRecord, MAX_ROOM_CAPACITY_FIELDS);

  if (isFiniteNumber(capacityFromField)) {
    return capacityFromField;
  }

  const capacities = roomTypeRecord.capacities;
  if (!Array.isArray(capacities)) {
    return undefined;
  }

  return getMaxDefinedNumber(capacities.map(getCapacityTotal));
}

function findRoomRate(roomType?: App.RoomType, roomRateId?: string) {
  if (!roomType || !roomRateId || !Array.isArray(roomType.room_rates)) {
    return undefined;
  }

  return roomType.room_rates.find((roomRate) => String(roomRate.id) === roomRateId);
}

function readIncludedGuestsCapacity(roomRate?: App.RoomRate) {
  const includedGuests = (roomRate as unknown as RecordLike | undefined)?.included_guests;

  if (!Array.isArray(includedGuests)) {
    return undefined;
  }

  return getMaxDefinedNumber(includedGuests.map(getCapacityTotal));
}

function getCapacityTotal(value: unknown) {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const record = value as RecordLike;
  const explicitTotal = readNumber(record, ['total', 'total_guests', 'max_guests']);

  if (isFiniteNumber(explicitTotal)) {
    return explicitTotal;
  }

  const adults = readNumber(record, ['adults']) ?? 0;
  const children = readNumber(record, ['children']) ?? 0;
  const teenagers = readNumber(record, ['teenagers']) ?? 0;

  return adults + children + teenagers;
}

function getMaxDefinedNumber(values: Array<number | undefined>) {
  const numericValues = values.filter(isFiniteNumber);

  return numericValues.length > 0 ? Math.max(...numericValues) : undefined;
}

function readString(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const value = record[field];

    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return undefined;
}

function readNumber(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const value = record[field];
    const numberValue = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;

    if (isFiniteNumber(numberValue)) {
      return numberValue;
    }
  }

  return undefined;
}

function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}
